import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  OutputSummary,
  RowExplanation,
  StructuredIngestTable,
} from '@/services/api/structuredIngest'
import { LeftOutRowCells } from './LeftOutRows'
import { RecipeGraph } from './RecipeGraph'
import { RowLens } from './RowLens'
import { graphFromSummary } from './recipe-graph-data'
import type { LensView, StructuredIngestActions } from './StructuredIngestView'

export function MappingDrawing({
  data,
  selection,
  onLensChange,
}: {
  data: { summary: OutputSummary; table: StructuredIngestTable; lens: LensView | null }
  selection: { id: string | null; onSelect: (id: string | null) => void }
  onLensChange: StructuredIngestActions['onLensChange']
}) {
  const { t } = useTranslation()
  const graph = useMemo(() => graphFromSummary(data.summary, t), [data.summary, t])
  return (
    <div className="shrink-0 space-y-2 p-3">
      {data.lens ? (
        <RowLens
          explanation={data.lens.explanation}
          loading={data.lens.loading}
          position={data.lens.position}
          total={data.table.facts.rows}
          onPositionChange={position => onLensChange(data.table.tableId, position)}
          selectedId={selection.id}
          onSelect={selection.onSelect}
        />
      ) : (
        <RecipeGraph
          data={graph}
          selectedId={selection.id}
          onSelect={selection.onSelect}
          className="h-80 md:h-64"
        />
      )}
      {data.lens?.explanation && <RowCaption explanation={data.lens.explanation} />}
      {data.lens?.explanation?.left_out && (
        <LeftOutRowCells cells={data.lens.explanation.left_out} />
      )}
    </div>
  )
}

function RowCaption({ explanation }: { explanation: RowExplanation }) {
  const { t } = useTranslation()
  const entities = explanation.entities.filter(
    entity => entity.enabled !== false && !entity.missing
  )
  const relationships = explanation.relationships.filter(
    relationship => relationship.enabled !== false && relationship.in_scope
  )
  return (
    <p className="text-xs text-muted-foreground">
      {t('tableMapping.lens.caption', {
        row: explanation.position + 1,
        entities: t('tableMapping.count.entities', { count: entities.length }),
        relationships: t('tableMapping.count.relationships', { count: relationships.length }),
      })}
    </p>
  )
}
